import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Toast.module.css";

export type ToastTone = "success" | "error";

export type ToastData = {
  id: number;
  message: string;
  tone: ToastTone;
};

type UseToastResult = {
  toast: ToastData | null;
  showToast: (message: string, tone?: ToastTone) => void;
  dismissToast: () => void;
};

// Single-slot toast state: a new message replaces the current one and
// restarts its lifetime (the Toast below remounts via the id key).
export const useToast = (): UseToastResult => {
  const [toast, setToast] = useState<ToastData | null>(null);
  const nextId = useRef<number>(0);

  const showToast = useCallback((message: string, tone: ToastTone = "success") => {
    nextId.current += 1;
    setToast({ id: nextId.current, message, tone });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  return { toast, showToast, dismissToast };
};

// Heuristic tone for call sites that only hand us a message string (the
// admin modals' onFlash plumbing): failure-sounding copy renders the
// error variant, everything else the success one.
export const toneForMessage = (message: string): ToastTone =>
  /\b(fail|failed|error|unable|invalid|denied)\b/i.test(message) ? "error" : "success";

const AUTO_DISMISS_MS = 4000;

type ToastProps = {
  toast: ToastData | null;
  onDismiss: () => void;
};

// Fixed bottom-center notification rendered in a portal so it never
// shifts page layout. Auto-dismisses (the draining underline shows the
// remaining lifetime); click anywhere on it to dismiss early.
export const Toast = ({ toast, onDismiss }: ToastProps): JSX.Element | null => {
  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [toast, onDismiss]);

  if (!toast) return null;

  const toneClass = toast.tone === "error" ? styles.toast_error : styles.toast_success;
  return createPortal(
    <div
      key={toast.id}
      className={`${styles.toast} ${toneClass}`}
      role="status"
      onClick={onDismiss}
      title="Dismiss"
    >
      <span className={styles.icon} aria-hidden="true">
        {toast.tone === "error" ? "!" : "✓"}
      </span>
      <span className={styles.message}>{toast.message}</span>
      <span className={styles.timer} aria-hidden="true" />
    </div>,
    document.body,
  );
};
